import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { useWhatsAppNumber, buildWaLink } from "@/lib/whatsapp";
import { toast } from "sonner";
import { MessageCircle, Phone, Mail, MapPin, Send } from "lucide-react";

export default function Contact() {
  const number = useWhatsAppNumber();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-oshveda-bg">
      <section className="bg-oshveda-bgAlt">
        <div className="container-x py-20 md:py-24">
          <div className="label-eyebrow mb-3">Get in touch</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-oshveda-ink leading-tight max-w-3xl">
            We'd love to <em className="text-oshveda-accent not-italic">hear from you.</em>
          </h1>
          <p className="text-oshveda-ink2 text-lg mt-4 max-w-2xl">
            Questions about a product? Wholesale enquiry? Need a personal Ayurvedic consultation? Reach out — we respond within hours.
          </p>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5 space-y-6">
            <a
              href={number ? buildWaLink(number, "Hello Oshveda Naturals, I have a question.") : "#"}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="block bg-white border border-oshveda-borderSubtle rounded-2xl p-6 hover:border-whatsapp transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <MessageCircle className="text-whatsapp" size={20} />
                <h3 className="font-serif text-xl">WhatsApp</h3>
              </div>
              <p className="text-sm text-oshveda-ink2">Fastest way to reach us. Chat with our team for orders & queries.</p>
              <p className="mt-3 text-sm font-semibold text-whatsapp">+91 70150 96120</p>
            </a>

            <a href="tel:+917015096120" data-testid="contact-call" className="block bg-white border border-oshveda-borderSubtle rounded-2xl p-6 hover:border-oshveda-primary transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <Phone className="text-oshveda-primary" size={20} />
                <h3 className="font-serif text-xl">Call us</h3>
              </div>
              <p className="text-sm text-oshveda-ink2">Mon–Sat, 10am–7pm IST.</p>
              <p className="mt-3 text-sm font-semibold text-oshveda-primary">+91 70150 96120</p>
            </a>

            <div className="bg-white border border-oshveda-borderSubtle rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <Mail className="text-oshveda-accent" size={20} />
                <h3 className="font-serif text-xl">Email</h3>
              </div>
              <p className="text-sm font-semibold text-oshveda-accent">hello@oshvedanaturals.store</p>
            </div>

            <div className="bg-white border border-oshveda-borderSubtle rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <MapPin className="text-oshveda-ink2" size={20} />
                <h3 className="font-serif text-xl">Address</h3>
              </div>
              <p className="text-sm text-oshveda-ink2">Oshveda Naturals, India.<br />Ships nationwide. Cash on Delivery available.</p>
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-10 border border-oshveda-borderSubtle" data-testid="contact-form">
              <h2 className="font-serif text-3xl text-oshveda-ink mb-2">Send us a message</h2>
              <p className="text-oshveda-ink2 mb-8">We'll respond within 24 hours, usually faster.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <label className="block">
                  <span className="label-eyebrow block mb-2">Name</span>
                  <input
                    type="text" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name"
                    className="w-full px-4 py-3 bg-oshveda-bgAlt rounded-xl border border-transparent focus:outline-none focus:border-oshveda-primary transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="label-eyebrow block mb-2">Phone</span>
                  <input
                    type="tel" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="contact-phone"
                    className="w-full px-4 py-3 bg-oshveda-bgAlt rounded-xl border border-transparent focus:outline-none focus:border-oshveda-primary transition-colors"
                  />
                </label>
              </div>
              <label className="block mb-5">
                <span className="label-eyebrow block mb-2">Email</span>
                <input
                  type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                  className="w-full px-4 py-3 bg-oshveda-bgAlt rounded-xl border border-transparent focus:outline-none focus:border-oshveda-primary transition-colors"
                />
              </label>
              <label className="block mb-6">
                <span className="label-eyebrow block mb-2">Message</span>
                <textarea
                  required rows={5} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  className="w-full px-4 py-3 bg-oshveda-bgAlt rounded-xl border border-transparent focus:outline-none focus:border-oshveda-primary transition-colors resize-none"
                />
              </label>
              <button
                type="submit" disabled={submitting} data-testid="contact-submit"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-oshveda-primary text-oshveda-bg text-sm tracking-widest uppercase hover:bg-oshveda-primaryHover disabled:opacity-60 hover:-translate-y-0.5 transition-all"
              >
                {submitting ? "Sending…" : <>Send message <Send size={14} /></>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
